// src/pages/Reports/TrendSeasonalityReportPage.js
//
// Trend & Seasonality Report UI
// ============================================================================
// PURPOSE
// -------
// This page presents time-based trend and seasonality analytics
// based on realized visits.
//
// MAIN BUSINESS QUESTIONS
// -----------------------
// - Are revenue and visit count trending up or down?
// - Which weeks are strongest or weakest?
// - Which months are strongest or weakest?
// - Which weekdays are structurally strongest?
//
// WHY THIS PAGE IS IMPORTANT
// --------------------------
// Operational reports often explain:
// - what is strong now,
// - which slots are valuable,
// - where losses happen.
//
// This page adds the time dimension:
// - how business evolves across weeks,
// - how months compare,
// - how weekdays behave across the full period.
//
// CURRENT BACKEND SHAPE
// ---------------------
// {
//   period,
//   filters,
//   methodology,
//   summary,
//   weekly_rows,
//   monthly_rows,
//   weekday_rows
// }
//
// UI GOALS
// --------
// 1) Keep the page visually aligned with your other report pages.
// 2) Make trend direction understandable at a glance.
// 3) Present week / month / weekday views as separate management blocks.
// 4) Keep detailed tables readable without clutter.
// 5) Add visual trend components without losing the value of tables.
// 6) Use charts as a fast-management layer and tables as the detail layer.
//
// VISUALIZATION STRATEGY
// ----------------------
// We intentionally add three chart types:
//
// 1) Weekly trend chart
//    - line chart
//    - great for seeing movement over time
//
// 2) Monthly seasonality chart
//    - bar chart
//    - great for comparing month blocks
//
// 3) Weekday seasonality chart
//    - horizontal bar chart
//    - great for ranking weekdays in a compact way
//
// IMPORTANT
// ---------
// The charts are intentionally simple and managerial.
// We avoid over-complicated charting because the main purpose here is quick,
// confident business reading rather than analytical experimentation.
//

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../../context/AuthContext";
import { fetchUsers } from "../../services/userService";
import { fetchTrendSeasonalityReport } from "../../services/reportService";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";

import "./TrendSeasonalityReportPage.css";

// I18N NOTE
// ---------
// This page translates frontend-owned labels, filters, empty states, table
// headers, chart legends, and backend-provided methodology note_keys. Backend
// date labels and payload field names remain unchanged to protect the
// report-service contract. The legacy backend notes array remains supported as
// a fallback during staged deployments.

/* -------------------------------------------------------------------------- */
/* Small formatting helpers                                                   */
/* -------------------------------------------------------------------------- */

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

function formatMinutes(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.0";
  return n.toFixed(1);
}

function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return String(Math.round(n));
}

function trendBadgeClassName(value) {
  const raw = String(value || "").toLowerCase();

  if (raw === "up") return "trend-badge trend-badge--up";
  if (raw === "down") return "trend-badge trend-badge--down";
  return "trend-badge trend-badge--flat";
}

function topRowLabel(row) {
  if (!row) return "—";
  return `${row.label} • ${formatMoney(row.total_revenue)} PLN`;
}

/**
 * Format numbers inside chart tooltip in a way that stays readable.
 */
function formatTooltipValue(value, name, t) {
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) return [value, name];

  if (
    name === t("reports:trendSeasonality.chart.revenue") ||
    name === t("reports:trendSeasonality.chart.totalRevenue") ||
    name === t("reports:trendSeasonality.chart.avgRevenuePerVisit") ||
    name === t("reports:trendSeasonality.chart.revenuePerBookedHour")
  ) {
    return [`${formatMoney(numeric)} PLN`, name];
  }

  if (
    name === t("reports:trendSeasonality.chart.bookedMinutes") ||
    name === t("reports:trendSeasonality.chart.avgMinutesPerVisit")
  ) {
    return [`${formatMinutes(numeric)} min`, name];
  }

  return [formatNumber(numeric), name];
}

/**
 * Shared tooltip label formatter.
 */
function formatTooltipLabel(label) {
  return String(label || "");
}

function formatWeeklyTooltipLabel(label, payload) {
  const row = payload?.[0]?.payload;

  if (row?.weekStart && row?.weekEnd) {
    return `${label} | ${row.weekStart} → ${row.weekEnd}`;
  }

  return String(label || "");
}

const CHART_COLORS = Object.freeze({
  revenue: "#2563eb",
  visits: "#16a34a",
  revenuePerHour: "#f59e0b",

  revenueBar: "#2563eb",
  weekdayRevenueBar: "#0ea5e9",

  grid: "#e5e7eb",
  axis: "#6b7280",
  tooltipBorder: "#d1d5db",
  tooltipBg: "#ffffff",
});

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const TrendSeasonalityReportPage = () => {
  const { t } = useTranslation(["common", "reports"]);
  const { authState } = useContext(AuthContext);

  const currentRole = authState?.user?.role || "";
  const currentUserId = authState?.user?.id;

  const allowedEvaluationModes = useMemo(() => {
    if (currentRole === "user") {
      return [EVALUATION_PERIOD_TYPES.MONTH];
    }

    return [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ];
  }, [currentRole, t]);

  const evaluationPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: allowedEvaluationModes,
  });

  const { from: selectedFrom, to: selectedTo } = evaluationPeriod;

  const [filters, setFilters] = useState(() => ({
    userIds:
      currentRole === "user" && currentUserId != null
        ? [currentUserId]
        : [],
    searchText: "",
  }));

  const [users, setUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [loadedPeriodMeta, setLoadedPeriodMeta] = useState({
    from: selectedFrom,
    to: selectedTo,
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  /* ------------------------------------------------------------------------ */
  /* Keep userIds aligned for role === "user"                                 */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (currentRole === "user" && currentUserId != null) {
      setFilters((prev) => ({
        ...prev,
        userIds: [currentUserId],
      }));
    }
  }, [currentRole, currentUserId]);

  /* ------------------------------------------------------------------------ */
  /* Load users for employee filter                                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await fetchUsers();
        setUsers(Array.isArray(userList) ? userList : []);
      } catch (err) {
        console.error("[TrendSeasonalityReportPage] ❌ Failed to fetch users:", err);
        setError(t("reports:trendSeasonality.errors.failedToLoadUsers"));
      }
    };

    if (currentRole !== "user") {
      loadUsers();
    }
  }, [currentRole, t]);

  const currentUserDisplay = useMemo(() => {
    const firstName = authState?.user?.first_name || "";
    const lastName = authState?.user?.last_name || "";
    const username = authState?.user?.username || "";

    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

    if (fullName && username) return `${fullName} (${username})`;
    if (fullName) return fullName;
    if (username) return username;

    return t("reports:common.currentUser", { defaultValue: "Current user" });
  }, [
    authState?.user?.first_name,
    authState?.user?.last_name,
    authState?.user?.username,
    t,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Fetch report                                                             */
  /* ------------------------------------------------------------------------ */

  const loadReportByRange = useCallback(
    async ({ from, to, userIds, displayLabel }) => {
      if (!from || !to) {
        setError(t("reports:common.invalidEvaluationPeriod"));
        return;
      }

      setLoading(true);
      setError("");

      try {
        const payload = await fetchTrendSeasonalityReport(from, to, userIds);
        setReport(payload || null);

        setLoadedPeriodMeta({
          from,
          to,
          displayLabel: displayLabel || "",
        });
      } catch (err) {
        console.error("[TrendSeasonalityReportPage] ❌ Error fetching report:", err);
        setError(t("reports:trendSeasonality.errors.failedToFetch"));
        setReport(null);
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  const initialRequestRef = useRef({
    from: selectedFrom,
    to: selectedTo,
    userIds:
      currentRole === "user" && currentUserId != null
        ? [currentUserId]
        : [],
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  useEffect(() => {
    loadReportByRange(initialRequestRef.current);
  }, [loadReportByRange]);

  /* ------------------------------------------------------------------------ */
  /* Derived data                                                             */
  /* ------------------------------------------------------------------------ */

  const summary = report?.summary || {};
  const methodologyNotes = useMemo(() => {
    const methodology = report?.methodology || {};
    const reportKind = methodology.report_kind || "trend_seasonality";

    /**
     * Preferred path for the i18n migration:
     * backend sends stable note_keys, frontend owns the visible language.
     * The legacy notes array remains supported so this page continues to work
     * against older backend payloads during staged deployments.
     */
    if (Array.isArray(methodology.note_keys) && methodology.note_keys.length > 0) {
      return methodology.note_keys.map((noteKey) => {
        const translatedNote = t(
          `reports:methodology.${reportKind}.notes.${noteKey}`,
          { defaultValue: "" }
        );

        return translatedNote || String(noteKey);
      });
    }

    return Array.isArray(methodology.notes) ? methodology.notes : [];
  }, [report, t]);

  const weeklyRows = Array.isArray(report?.weekly_rows) ? report.weekly_rows : [];
  const monthlyRows = Array.isArray(report?.monthly_rows) ? report.monthly_rows : [];
  const weekdayRows = Array.isArray(report?.weekday_rows) ? report.weekday_rows : [];

  const visibleWeeklyRows = useMemo(() => {
    const searchText = String(filters.searchText || "").trim().toLowerCase();
    if (!searchText) return weeklyRows;

    return weeklyRows.filter((row) =>
      String(row.label || "").toLowerCase().includes(searchText)
    );
  }, [weeklyRows, filters.searchText]);

  const visibleMonthlyRows = useMemo(() => {
    const searchText = String(filters.searchText || "").trim().toLowerCase();
    if (!searchText) return monthlyRows;

    return monthlyRows.filter((row) =>
      String(row.label || "").toLowerCase().includes(searchText)
    );
  }, [monthlyRows, filters.searchText]);

  const visibleWeekdayRows = useMemo(() => {
    const searchText = String(filters.searchText || "").trim().toLowerCase();
    if (!searchText) return weekdayRows;

    return weekdayRows.filter((row) =>
      String(row.label || "").toLowerCase().includes(searchText)
    );
  }, [weekdayRows, filters.searchText]);

  /**
   * Weekly chart data
   * -----------------
   * We keep a compact shape that works well with Recharts.
   */
  const weeklyChartData = useMemo(() => {
    return visibleWeeklyRows.map((row) => ({
      label: row.label,
      weekStart: row.week_start || null,
      weekEnd: row.week_end || null,
      revenue: Number(row.total_revenue) || 0,
      visits: Number(row.total_visits) || 0,
      revenuePerHour: Number(row.revenue_per_booked_hour) || 0,
    }));
  }, [visibleWeeklyRows]);

  /**
   * Monthly chart data
   * ------------------
   * Bar chart is the clearest visual for month blocks.
   */
  const monthlyChartData = useMemo(() => {
    return visibleMonthlyRows.map((row) => ({
      label: row.label,
      revenue: Number(row.total_revenue) || 0,
      visits: Number(row.total_visits) || 0,
      bookedMinutes: Number(row.total_booked_minutes) || 0,
    }));
  }, [visibleMonthlyRows]);

  /**
   * Weekday chart data
   * ------------------
   * Horizontal bar chart is ideal for ranking categories like weekdays.
   */
  const weekdayChartData = useMemo(() => {
    return visibleWeekdayRows.map((row) => ({
      label: row.label,
      revenue: Number(row.total_revenue) || 0,
      visits: Number(row.total_visits) || 0,
      revenuePerHour: Number(row.revenue_per_booked_hour) || 0,
    }));
  }, [visibleWeekdayRows]);

  const hasReportData = Boolean(report);

  /* ------------------------------------------------------------------------ */
  /* Render helper                                                            */
  /* ------------------------------------------------------------------------ */

  const renderTrendTable = (rows, emptyText) => (
    <div className="table-wrapper">
      <table className="data-table data-table--compact">
        <thead>
          <tr>
            <th>{t("reports:trendSeasonality.table.period")}</th>
            <th>{t("reports:trendSeasonality.table.visits")}</th>
            <th>{t("reports:trendSeasonality.table.revenuePln")}</th>
            <th>{t("reports:trendSeasonality.table.bookedMinutes")}</th>
            <th>{t("reports:trendSeasonality.table.avgRevenuePerVisit")}</th>
            <th>{t("reports:trendSeasonality.table.avgMinutesPerVisit")}</th>
            <th>{t("reports:trendSeasonality.table.revenuePerBookedHour")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td>{formatCount(row.total_visits)}</td>
                <td>{formatMoney(row.total_revenue)}</td>
                <td>{formatMinutes(row.total_booked_minutes)}</td>
                <td>{formatMoney(row.avg_revenue_per_visit)}</td>
                <td>{formatMinutes(row.avg_minutes_per_visit)}</td>
                <td>{formatMoney(row.revenue_per_booked_hour)} PLN/h</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="empty-table-cell">
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="trend-seasonality-page">
      <div className="trend-seasonality-header">
        <div className="header-left">
          <h1>{t("reports:trendSeasonality.title")}</h1>
          <p className="header-hint">{t("reports:trendSeasonality.headerHint")}</p>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="filters-bar">
        <div className="filters-left">
          <EvaluationPeriodPicker period={evaluationPeriod} />

          {currentRole !== "user" ? (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employees")}</label>

              <select
                multiple
                className="user-dropdown"
                value={filters.userIds}
                onChange={(e) => {
                  const selectedValues = Array.from(
                    e.target.selectedOptions,
                    (o) => Number(o.value)
                  ).filter(Number.isFinite);

                  setFilters((prev) => ({
                    ...prev,
                    userIds: selectedValues,
                  }));
                }}
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.username})
                  </option>
                ))}
              </select>

              <div className="filter-help">{t("reports:common.multiSelectHelp")}</div>
            </div>
          ) : (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employee")}</label>
              <input
                type="text"
                className="user-fixed"
                value={currentUserDisplay}
                readOnly
              />
            </div>
          )}

          <div className="filter-item filter-item-search">
            <label className="filter-label">{t("reports:common.search")}</label>
            <input
              type="text"
              className="search-input"
              value={filters.searchText}
              placeholder={t("reports:trendSeasonality.searchPlaceholder")}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  searchText: e.target.value,
                }))
              }
            />
          </div>
        </div>

        <div className="filters-actions">
          <button
            className="apply-button"
            onClick={() =>
              loadReportByRange({
                from: selectedFrom,
                to: selectedTo,
                userIds: filters.userIds,
                displayLabel: evaluationPeriod.displayLabel,
              })
            }
            disabled={loading}
          >
            {loading ? t("reports:common.loading") : t("reports:common.generate")}
          </button>
        </div>
      </div>

      <div className="report-scroll">
        {loading ? (
          <div className="loading-panel">{t("reports:common.loading")}</div>
        ) : hasReportData ? (
          <>
            <div className="summary-grid">
              <div className="summary-card">
                <span className="summary-label">{t("reports:trendSeasonality.totalVisits")}</span>
                <span className="summary-value">
                  {formatCount(summary.total_visits)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:trendSeasonality.totalRevenue")}</span>
                <span className="summary-value">
                  {formatMoney(summary.total_revenue)} PLN
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:trendSeasonality.totalBookedMinutes")}</span>
                <span className="summary-value">
                  {formatMinutes(summary.total_booked_minutes)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:trendSeasonality.weeks")}</span>
                <span className="summary-value">
                  {formatCount(summary.weeks_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:trendSeasonality.months")}</span>
                <span className="summary-value">
                  {formatCount(summary.months_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:trendSeasonality.revenueTrend")}</span>
                <span className="summary-value summary-value--small">
                  <span className={trendBadgeClassName(summary.revenue_trend_direction)}>
                    {t(`reports:trendSeasonality.trend.${summary.revenue_trend_direction || "flat"}`, { defaultValue: summary.revenue_trend_direction || "flat" })}
                  </span>
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:trendSeasonality.visitsTrend")}</span>
                <span className="summary-value summary-value--small">
                  <span className={trendBadgeClassName(summary.visits_trend_direction)}>
                    {t(`reports:trendSeasonality.trend.${summary.visits_trend_direction || "flat"}`, { defaultValue: summary.visits_trend_direction || "flat" })}
                  </span>
                </span>
              </div>
            </div>

            <div className="highlight-grid">
              <div className="highlight-card">
                <div className="highlight-title">{t("reports:trendSeasonality.strongestWeek")}</div>
                <div className="highlight-name">{topRowLabel(summary.strongest_week)}</div>
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:trendSeasonality.weakestWeek")}</div>
                <div className="highlight-name">{topRowLabel(summary.weakest_week)}</div>
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:trendSeasonality.strongestMonth")}</div>
                <div className="highlight-name">{topRowLabel(summary.strongest_month)}</div>
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:trendSeasonality.strongestWeekday")}</div>
                <div className="highlight-name">{topRowLabel(summary.strongest_weekday)}</div>
              </div>
            </div>

            <div className="methodology-card">
              <div className="methodology-title">{t("reports:trendSeasonality.methodology")}</div>
              <ul className="methodology-list">
                {methodologyNotes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>

              {(loadedPeriodMeta?.from || loadedPeriodMeta?.to) && (
                <div className="loaded-period-note">
                  {t("reports:common.loadedPeriod")}: <strong>{loadedPeriodMeta.from}</strong> →{" "}
                  <strong>{loadedPeriodMeta.to}</strong>
                  {loadedPeriodMeta.displayLabel ? (
                    <> ({loadedPeriodMeta.displayLabel})</>
                  ) : null}
                </div>
              )}
            </div>

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:trendSeasonality.weeklyTrendTitle")}</h2>
                <p>{t("reports:trendSeasonality.weeklyTrendDescription")}</p>
              </div>

              <div className="chart-panel">
                <div className="chart-panel__title">{t("reports:trendSeasonality.weeklyRevenueVisitsTrend")}</div>
                <div className="chart-container chart-container--large">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weeklyChartData} margin={{ top: 10, right: 24, left: 0, bottom: 8 }}>
                    <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fill: CHART_COLORS.axis, fontSize: 12 }} />
                    <YAxis
                      yAxisId="left"
                      tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value, name) => formatTooltipValue(value, name, t)}
                      labelFormatter={(label, payload) => formatWeeklyTooltipLabel(label, payload)}
                      contentStyle={{
                        backgroundColor: CHART_COLORS.tooltipBg,
                        border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                        borderRadius: 8,
                      }}
                    />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="revenue"
                      name={t("reports:trendSeasonality.chart.revenue")}
                      stroke={CHART_COLORS.revenue}
                      strokeWidth={2}
                      dot={{ r: 2, fill: CHART_COLORS.revenue }}
                      activeDot={{ r: 5, fill: CHART_COLORS.revenue }}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="visits"
                      name={t("reports:trendSeasonality.chart.visits")}
                      stroke={CHART_COLORS.visits}
                      strokeWidth={2}
                      dot={{ r: 2, fill: CHART_COLORS.visits }}
                      activeDot={{ r: 5, fill: CHART_COLORS.visits }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                </div>
              </div>

              {renderTrendTable(
                visibleWeeklyRows,
                t("reports:trendSeasonality.noWeeklyRows")
              )}
            </div>

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:trendSeasonality.monthlyTrendTitle")}</h2>
                <p>{t("reports:trendSeasonality.monthlyTrendDescription")}</p>
              </div>

              <div className="chart-panel">
                <div className="chart-panel__title">{t("reports:trendSeasonality.monthlyRevenueSeasonality")}</div>
                <div className="chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyChartData} margin={{ top: 10, right: 24, left: 0, bottom: 8 }}>
                    <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fill: CHART_COLORS.axis, fontSize: 12 }} />
                    <YAxis tick={{ fill: CHART_COLORS.axis, fontSize: 12 }} />
                    <Tooltip
                      formatter={(value) => [`${formatMoney(value)} PLN`, t("reports:trendSeasonality.chart.revenue")]}
                      labelFormatter={formatTooltipLabel}
                      contentStyle={{
                        backgroundColor: CHART_COLORS.tooltipBg,
                        border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                        borderRadius: 8,
                      }}
                    />
                    <Legend />
                    <Bar
                      dataKey="revenue"
                      name={t("reports:trendSeasonality.chart.revenue")}
                      fill={CHART_COLORS.revenueBar}
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
                </div>
              </div>

              {renderTrendTable(
                visibleMonthlyRows,
                t("reports:trendSeasonality.noMonthlyRows")
              )}
            </div>

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:trendSeasonality.weekdaySeasonalityTitle")}</h2>
                <p>{t("reports:trendSeasonality.weekdaySeasonalityDescription")}</p>
              </div>

              <div className="chart-panel">
                <div className="chart-panel__title">{t("reports:trendSeasonality.weekdayRevenueRanking")}</div>
                <div className="chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={weekdayChartData}
                    layout="vertical"
                    margin={{ top: 10, right: 24, left: 24, bottom: 8 }}
                  >
                    <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fill: CHART_COLORS.axis, fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={90}
                      tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value) => [`${formatMoney(value)} PLN`, t("reports:trendSeasonality.chart.revenue")]}
                      labelFormatter={formatTooltipLabel}
                      contentStyle={{
                        backgroundColor: CHART_COLORS.tooltipBg,
                        border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                        borderRadius: 8,
                      }}
                    />
                    <Legend />
                    <Bar
                      dataKey="revenue"
                      name={t("reports:trendSeasonality.chart.revenue")}
                      fill={CHART_COLORS.weekdayRevenueBar}
                      radius={[0, 8, 8, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
                </div>
              </div>

              {renderTrendTable(
                visibleWeekdayRows,
                t("reports:trendSeasonality.noWeekdayRows")
              )}
            </div>
          </>
        ) : (
          <div className="empty-panel">{t("reports:common.noDataAvailable")}</div>
        )}
      </div>
    </div>
  );
};

export default TrendSeasonalityReportPage;

